import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publicGet, publicPost } from '@/lib/api/publicClient';
import { publicScheduleRequestEndpoints } from '@/lib/api/endpoints';
import type {
  AvailabilityResult,
  AvailabilitySlot,
  ScheduleRequestContext,
} from '@/types/scheduleRequest';
import type { RescheduleRequestInput } from './useConfirmActions';

/**
 * What the book/reschedule API needs: just the opaque sealed handle. The
 * server opens it to recover the slot, the technician AND the offered window,
 * so nothing else about the offer travels in the body — there is no
 * `technicianId`, `startAt` or `endAt` to echo. Everything else on the slot
 * (`timezone`, `label`, the times) is display-only and must NOT be sent: the
 * API whitelists its body and rejects unknown fields ("property startAt should
 * not exist").
 */
export type SlotSelection = Pick<AvailabilitySlot, 'slotId'> & {
  /** Book at one of the contact's other service addresses (see context). */
  addressId?: string;
};

const ctxKey = (token: string) => ['public', 'schedule-request', token];

/** Booking page context: branding, status, policy, appointment (if booked). */
export function useScheduleRequestContext(token: string) {
  return useQuery<ScheduleRequestContext>({
    queryKey: ctxKey(token),
    queryFn: async () => {
      const res = await publicGet<{ data: ScheduleRequestContext }>(
        publicScheduleRequestEndpoints.context(token),
      );
      return res.data;
    },
    retry: false,
    enabled: Boolean(token),
  });
}

/** Open slots for a date range. POST because it carries the range in the body. */
export function useScheduleAvailability(
  token: string,
  dateFrom: string,
  dateTo: string,
  enabled: boolean,
  addressId?: string,
) {
  return useQuery<AvailabilityResult>({
    queryKey: ['public', 'schedule-request', token, 'availability', dateFrom, dateTo, addressId],
    queryFn: () =>
      publicPost<AvailabilityResult>(publicScheduleRequestEndpoints.availability(token), {
        dateFrom,
        dateTo,
        ...(addressId ? { addressId } : {}),
      }),
    enabled: enabled && Boolean(token),
    retry: false,
  });
}

function useContextMutation(token: string, url: (token: string) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slot?: SlotSelection) => {
      const res = await publicPost<{ data: ScheduleRequestContext }>(url(token), slot ?? {});
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(ctxKey(token), data),
  });
}

export function useBookSlot(token: string) {
  return useContextMutation(token, publicScheduleRequestEndpoints.book);
}

export function useConfirmReservedAppointment(token: string) {
  return useContextMutation(token, publicScheduleRequestEndpoints.confirm);
}

export function useRescheduleSlot(token: string) {
  return useContextMutation(token, publicScheduleRequestEndpoints.reschedule);
}

export function useRequestScheduleReschedule(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RescheduleRequestInput) => {
      const res = await publicPost<{ data: ScheduleRequestContext }, RescheduleRequestInput>(
        publicScheduleRequestEndpoints.requestReschedule(token),
        input,
      );
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(ctxKey(token), data),
  });
}

export function useCancelBooking(token: string) {
  return useContextMutation(token, publicScheduleRequestEndpoints.cancel);
}
